import { useEffect, useRef, type CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar, CalendarDays, Users, ClipboardSignature } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { usePlaydatesBadge } from '@/hooks/usePlaydatesBadge';
import { FloatingAddButton } from '@/components/FloatingAddButton';

export const BottomNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef<HTMLElement | null>(null);
  const playdatesNewCount = usePlaydatesBadge();

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    let frame = 0;
    let repaintToggle = false;

    const pinToVisibleViewport = () => {
      frame = 0;

      // The bar always stays pinned to the bottom of the page — even when the
      // on-screen keyboard is open, it sits under the keyboard so search
      // results above it stay visible. iOS can occasionally keep a fixed,
      // blurred element painted at an old scroll position. A tiny alternating
      // nudge forces a repaint without visibly moving the bar.
      repaintToggle = !repaintToggle;
      nav.style.setProperty('--mobile-bottom-nav-repaint', repaintToggle ? '0.01px' : '0px');
    };

    const schedulePin = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(pinToVisibleViewport);
    };

    pinToVisibleViewport();

    window.addEventListener('scroll', schedulePin, { passive: true });
    window.addEventListener('resize', schedulePin);
    window.addEventListener('orientationchange', schedulePin);
    window.addEventListener('pageshow', schedulePin);
    window.visualViewport?.addEventListener('scroll', schedulePin, { passive: true });
    window.visualViewport?.addEventListener('resize', schedulePin);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedulePin);
      window.removeEventListener('resize', schedulePin);
      window.removeEventListener('orientationchange', schedulePin);
      window.removeEventListener('pageshow', schedulePin);
      window.visualViewport?.removeEventListener('scroll', schedulePin);
      window.visualViewport?.removeEventListener('resize', schedulePin);
    };
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItemClass = (active: boolean) =>
    `min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-full px-0 py-1 transition-all duration-200 hover:bg-transparent focus-visible:bg-transparent active:scale-95 ${
      active ? 'text-primary hover:text-primary' : 'text-muted-foreground hover:text-foreground'
    }`;

  const navIconClass = (active: boolean) =>
    `flex h-7 w-10 items-center justify-center transition-colors duration-200 ${
      active ? 'text-primary' : 'text-muted-foreground'
    }`;

  return (
    <nav
      ref={navRef}
      className="mobile-bottom-nav md:hidden fixed left-3 right-3 z-50 overflow-visible rounded-full border border-border/80 bg-background/95 shadow-[0_10px_30px_-12px_hsl(var(--foreground)/0.28)] backdrop-blur-xl supports-[backdrop-filter]:bg-background/90"
      style={{
        // Promote to its own compositing layer: iOS Safari otherwise mis-paints
        // fixed elements during momentum scrolling, leaving the bar mid-screen.
        transform: 'translate3d(0, var(--mobile-bottom-nav-repaint, 0px), 0)',
        WebkitTransform: 'translate3d(0, var(--mobile-bottom-nav-repaint, 0px), 0)',
        willChange: 'bottom, transform',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        contain: 'layout paint style',
      } as CSSProperties}
    >
      <div className="flex h-[4.25rem] items-center justify-around px-2 py-2">
        {/* Activities */}
        <Button
          variant="ghost"
          onClick={() => navigate('/activities')}
          className={navItemClass(isActive('/activities'))}
          aria-current={isActive('/activities') ? 'page' : undefined}
        >
          <span className={navIconClass(isActive('/activities'))}>
            <Calendar className="h-6 w-6" strokeWidth={isActive('/activities') ? 2.5 : 2} />
          </span>
          <span className={`text-[10px] leading-none ${isActive('/activities') ? 'font-semibold' : 'font-medium'}`}>
            Activities
          </span>
        </Button>

        {/* Community */}
        <Button
          variant="ghost"
          onClick={() => navigate('/community')}
          className={navItemClass(isActive('/community'))}
          aria-current={isActive('/community') ? 'page' : undefined}
        >
          <span className={navIconClass(isActive('/community'))}>
            <Users className="h-6 w-6" strokeWidth={isActive('/community') ? 2.5 : 2} />
          </span>
          <span className={`text-[10px] leading-none ${isActive('/community') ? 'font-semibold' : 'font-medium'}`}>
            Community
          </span>
        </Button>

        {/* Add — centred create button */}
        <FloatingAddButton variant="tab" />

        {/* Playdates */}
        <Button
          variant="ghost"
          onClick={() => navigate('/playdates')}
          className={navItemClass(isActive('/playdates'))}
          aria-current={isActive('/playdates') ? 'page' : undefined}
        >
          <span className={`${navIconClass(isActive('/playdates'))} relative`}>
            <CalendarDays className="h-6 w-6" strokeWidth={isActive('/playdates') ? 2.5 : 2} />
            {playdatesNewCount > 0 && (
              <Badge className="absolute -top-1 -right-0.5 h-4 min-w-[16px] flex items-center justify-center rounded-full border-0 bg-bright-fuschia p-0 px-1 text-[9px] font-bold leading-none text-white">
                {playdatesNewCount > 9 ? '9+' : playdatesNewCount}
              </Badge>
            )}
          </span>
          <span className={`text-[10px] leading-none ${isActive('/playdates') ? 'font-semibold' : 'font-medium'}`}>
            Playdates
          </span>
        </Button>

        {/* Noticeboard */}
        <Button
          variant="ghost"
          onClick={() => navigate('/noticeboard')}
          className={navItemClass(isActive('/noticeboard'))}
          aria-current={isActive('/noticeboard') ? 'page' : undefined}
        >
          <span className={navIconClass(isActive('/noticeboard'))}>
            <ClipboardSignature className="h-6 w-6" strokeWidth={isActive('/noticeboard') ? 2.5 : 2} />
          </span>
          <span className={`text-[10px] leading-none ${isActive('/noticeboard') ? 'font-semibold' : 'font-medium'}`}>
            Noticeboard
          </span>
        </Button>
      </div>
    </nav>
  );
};
